import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HeartPulse, Gauge, AlertTriangle, History, Search, TrendingUp, TrendingDown, Minus,
  Bell, Share2, MessageCircle, Mail, Link2, Linkedin, CheckCircle, FolderOpen,
  Droplets, PiggyBank, Scale, LineChart, Building2, Eye, ShieldCheck, Briefcase, Calculator, Home, Users
} from 'lucide-react';
import { track } from '../lib/analytics';

const T = {
  fr: {
    whyTitle: 'Une entreprise peut avoir l\'air solide. Que disent vraiment ses chiffres ?',
    whyText: 'Une belle image, une activité importante ou une présence impressionnante ne suffisent pas à connaître la santé financière d\'une entreprise. CheckCheck transforme les données disponibles en une analyse claire et compréhensible.',
    whyItems: [
      { icon: HeartPulse, t: 'Santé financière', d: 'Comprenez rapidement la situation générale de l\'entreprise.' },
      { icon: Gauge, t: 'Score CheckCheck', d: 'Obtenez une lecture synthétique de sa situation.' },
      { icon: AlertTriangle, t: 'Risque de défaillance', d: 'Identifiez les éventuels signaux de fragilité.' },
      { icon: History, t: 'Historique', d: 'Comprenez comment sa situation évolue.' },
    ],
    whyCta: 'Vérifier une entreprise',
    clearTitle: 'Des chiffres de comptable. Une réponse que tout le monde comprend.',
    fictional: 'Exemple fictif',
    inShort: 'En clair',
    inShortText: 'Les données disponibles indiquent une situation financière globalement solide.',
    solidLabel: 'Situation financière solide',
    indicators: [['Solvabilité', 'Bonne'], ['Liquidité', 'Bonne'], ['Rentabilité', 'Correcte'], ['Endettement', 'Maîtrisé']],
    featTitle: 'Tout ce que CheckCheck vous permet de voir',
    features: [
      [Gauge, 'Score CheckCheck', 'Une lecture immédiate de la santé financière.'],
      [HeartPulse, 'Santé financière', 'Comprendre les forces et fragilités principales.'],
      [AlertTriangle, 'Risque de défaillance', 'Identifier les signaux nécessitant de la vigilance.'],
      [ShieldCheck, 'Solvabilité', 'La capacité à faire face à ses engagements.'],
      [Droplets, 'Liquidité', 'Sa capacité à honorer ses obligations à court terme.'],
      [PiggyBank, 'Rentabilité', 'Voir si son activité génère durablement des résultats.'],
      [Scale, 'Endettement', 'Comprendre le poids de ses dettes.'],
      [History, 'Historique', 'Observer l\'évolution de sa situation dans le temps.'],
      [TrendingUp, 'Tendances', 'Amélioration, stabilité ou dégradation.'],
      [Eye, 'Points d\'attention', 'L\'important ressort sans analyser les comptes soi-même.'],
      [LineChart, 'Graphiques', 'Visualiser facilement les évolutions.'],
      [Building2, 'Données d\'entreprise', 'Les principales informations sur la société.'],
    ],
    forYouTitle: 'Une entreprise vous concerne ? CheckCheck peut vous être utile.',
    profiles: [
      [Briefcase, 'Entrepreneurs et dirigeants', 'Un nouveau client, un fournisseur, un partenaire ? Vérifiez sa santé financière avant de vous engager.', 'professional'],
      [Calculator, 'Comptables et professionnels du chiffre', 'Obtenez immédiatement une lecture synthétique d\'un dossier : rapidité, historique, surveillance, partage.', 'professional'],
      [Users, 'Commerciaux et indépendants', 'Un client demande 30 ou 60 jours pour payer ? Faites un CheckCheck avant de décider.', 'professional'],
      [Home, 'Particuliers', 'Travaux, acompte, achat coûteux ? Regardez d\'abord à qui vous allez verser votre argent.', 'private'],
      [Eye, 'Curieux', 'Cette entreprise a l\'air de cartonner ? Voyez ce que disent réellement ses chiffres.', 'curiosity'],
    ],
    forYouNote: 'Pas besoin d\'être comptable. CheckCheck traduit les données financières pour vous.',
    curioTitle: 'L\'apparence raconte une histoire. Les chiffres en racontent une autre.',
    curioText: 'Une entreprise peut sembler florissante. CheckCheck vous permet de découvrir ce que montrent réellement les données financières disponibles.',
    curioCta: 'Saisir le nom d\'une entreprise',
    watchTitle: 'Ne vérifiez pas seulement aujourd\'hui. Sachez aussi quand quelque chose change.',
    watchText: 'Ajoutez les entreprises importantes à votre surveillance et retrouvez leur évolution dans CheckCheck. Recevez une alerte lorsqu\'un changement important est détecté.',
    watchCta: 'Surveiller une entreprise',
    histTitle: 'CheckCheck se souvient pour vous.',
    histText: 'Retrouvez les entreprises que vous avez consultées et suivez l\'évolution de leur situation dans le temps.',
    listTitle: 'Organisez les entreprises qui comptent pour vous.',
    lists: ['Mes clients', 'Mes fournisseurs', 'Mes prospects', 'À surveiller'],
    listCta: 'Créer ma première liste',
    shareTitle: 'Une décision se prend rarement seule.',
    shareText: 'Partagez une analyse CheckCheck avec votre associé, votre comptable, votre client ou votre équipe. Le destinataire consulte le rapport en un clic.',
    howTitle: 'Comment ça marche ?',
    steps: [['Recherchez', 'Tapez le nom d\'une entreprise belge ou son numéro BCE/TVA.'], ['Comprenez', 'CheckCheck transforme les données financières en informations faciles à lire.'], ['Décidez', 'Informez-vous avant de travailler, payer ou vous engager avec l\'entreprise.']],
    freeTitle: 'Oui, CheckCheck est gratuit.',
    freeText: 'Notre priorité actuelle est simple : permettre au plus grand nombre de comprendre facilement la santé financière des entreprises belges.',
    freeItems: ['Recherches gratuites', 'Analyses accessibles gratuitement', 'Pas de carte bancaire', 'Accès immédiat'],
    freeCta: 'Faire mon premier CheckCheck',
    trustTitle: 'Des données complexes. Une lecture plus simple.',
    trustText: 'CheckCheck analyse les données publiques disponibles (BCE, comptes annuels BNB) pour vous aider à les comprendre. CheckCheck constitue un outil d\'information et d\'aide à la compréhension. L\'analyse ne remplace pas un conseil professionnel adapté à une situation particulière.',
    finalTitle: 'Une entreprise en tête ?',
    finalSub: 'Découvrez ce que disent ses chiffres.',
    finalCta: 'Vérifier une entreprise gratuitement',
    finalTag: 'Gratuit. Simple. Utile.',
  },
};
T.nl = {
  whyTitle: 'Een bedrijf kan er solide uitzien. Wat zeggen de cijfers echt?',
  whyText: 'Een mooi imago, veel activiteit of een indrukwekkende aanwezigheid zegt niets over de financiële gezondheid van een bedrijf. CheckCheck vertaalt de beschikbare gegevens naar een heldere, begrijpelijke analyse.',
  whyItems: [
    { icon: HeartPulse, t: 'Financiële gezondheid', d: 'Begrijp snel de algemene situatie van het bedrijf.' },
    { icon: Gauge, t: 'CheckCheck-score', d: 'Krijg een beknopte lezing van de situatie.' },
    { icon: AlertTriangle, t: 'Faalrisico', d: 'Herken mogelijke signalen van kwetsbaarheid.' },
    { icon: History, t: 'Historiek', d: 'Begrijp hoe de situatie evolueert.' },
  ],
  whyCta: 'Een bedrijf controleren',
  clearTitle: 'Boekhoudcijfers. Een antwoord dat iedereen begrijpt.',
  fictional: 'Fictief voorbeeld',
  inShort: 'Kort gezegd',
  inShortText: 'De beschikbare gegevens wijzen op een globaal solide financiële situatie.',
  solidLabel: 'Solide financiële situatie',
  indicators: [['Solvabiliteit', 'Goed'], ['Liquiditeit', 'Goed'], ['Rentabiliteit', 'Correct'], ['Schuldgraad', 'Beheerst']],
  featTitle: 'Alles wat CheckCheck u laat zien',
  features: [
    [Gauge, 'CheckCheck-score', 'Een onmiddellijke lezing van de financiële gezondheid.'],
    [HeartPulse, 'Financiële gezondheid', 'De belangrijkste sterktes en zwaktes begrijpen.'],
    [AlertTriangle, 'Faalrisico', 'Signalen herkennen die waakzaamheid vragen.'],
    [ShieldCheck, 'Solvabiliteit', 'Het vermogen om verplichtingen na te komen.'],
    [Droplets, 'Liquiditeit', 'De capaciteit om kortlopende verplichtingen te voldoen.'],
    [PiggyBank, 'Rentabiliteit', 'Zien of de activiteit duurzaam resultaten oplevert.'],
    [Scale, 'Schuldgraad', 'Het gewicht van de schulden begrijpen.'],
    [History, 'Historiek', 'De evolutie van de situatie doorheen de tijd volgen.'],
    [TrendingUp, 'Trends', 'Verbetering, stabiliteit of verslechtering.'],
    [Eye, 'Aandachtspunten', 'Het belangrijkste springt eruit zonder zelf de rekeningen te analyseren.'],
    [LineChart, 'Grafieken', 'Evoluties eenvoudig visualiseren.'],
    [Building2, 'Bedrijfsgegevens', 'De belangrijkste informatie over de onderneming.'],
  ],
  forYouTitle: 'Heeft een bedrijf invloed op u? CheckCheck kan u helpen.',
  profiles: [
    [Briefcase, 'Ondernemers en bedrijfsleiders', 'Een nieuwe klant, leverancier of partner? Controleer de financiële gezondheid vóór u zich engageert.', 'professional'],
    [Calculator, 'Boekhouders en cijferberoepen', 'Krijg meteen een beknopte lezing van een dossier: snelheid, historiek, opvolging, delen.', 'professional'],
    [Users, 'Verkopers en zelfstandigen', 'Vraagt een klant 30 of 60 dagen betalingsuitstel? Doe eerst een CheckCheck.', 'professional'],
    [Home, 'Particulieren', 'Werken, een voorschot, een dure aankoop? Kijk eerst aan wie u uw geld geeft.', 'private'],
    [Eye, 'Nieuwsgierigen', 'Lijkt dat bedrijf een succes? Bekijk wat de cijfers echt zeggen.', 'curiosity'],
  ],
  forYouNote: 'U hoeft geen boekhouder te zijn. CheckCheck vertaalt de financiële gegevens voor u.',
  curioTitle: 'De schijn vertelt één verhaal. De cijfers een ander.',
  curioText: 'Een bedrijf kan bloeiend lijken. Met CheckCheck ontdekt u wat de beschikbare financiële gegevens werkelijk tonen.',
  curioCta: 'Typ de naam van een bedrijf',
  watchTitle: 'Controleer niet alleen vandaag. Weet ook wanneer er iets verandert.',
  watchText: 'Voeg belangrijke bedrijven toe aan uw opvolging en volg hun evolutie in CheckCheck. Ontvang een melding wanneer een belangrijke wijziging wordt gedetecteerd.',
  watchCta: 'Een bedrijf opvolgen',
  histTitle: 'CheckCheck onthoudt het voor u.',
  histText: 'Vind de bedrijven terug die u raadpleegde en volg de evolutie van hun situatie doorheen de tijd.',
  listTitle: 'Organiseer de bedrijven die voor u tellen.',
  lists: ['Mijn klanten', 'Mijn leveranciers', 'Mijn prospecten', 'Op te volgen'],
  listCta: 'Mijn eerste lijst maken',
  shareTitle: 'Een beslissing neemt u zelden alleen.',
  shareText: 'Deel een CheckCheck-analyse met uw vennoot, boekhouder, klant of team. De ontvanger bekijkt het rapport in één klik.',
  howTitle: 'Hoe werkt het?',
  steps: [['Zoek', 'Typ de naam van een Belgisch bedrijf of het KBO/BTW-nummer.'], ['Begrijp', 'CheckCheck vertaalt de financiële gegevens naar leesbare informatie.'], ['Beslis', 'Informeer u vóór u samenwerkt, betaalt of zich engageert.']],
  freeTitle: 'Ja, CheckCheck is gratis.',
  freeText: 'Onze huidige prioriteit is eenvoudig: zoveel mogelijk mensen de financiële gezondheid van Belgische bedrijven laten begrijpen.',
  freeItems: ['Gratis zoekopdrachten', 'Gratis toegankelijke analyses', 'Geen bankkaart', 'Onmiddellijke toegang'],
  freeCta: 'Mijn eerste CheckCheck doen',
  trustTitle: 'Complexe gegevens. Een eenvoudigere lezing.',
  trustText: 'CheckCheck analyseert de beschikbare openbare gegevens (KBO, jaarrekeningen NBB) om u te helpen ze te begrijpen. CheckCheck is een informatie- en begripstool. De analyse vervangt geen professioneel advies op maat.',
  finalTitle: 'Een bedrijf in gedachten?',
  finalSub: 'Ontdek wat de cijfers zeggen.',
  finalCta: 'Gratis een bedrijf controleren',
  finalTag: 'Gratis. Eenvoudig. Nuttig.',
};

const Sec = ({ id, children, className = '' }) => (
  <section data-section={id} className={`w-full max-w-2xl mx-auto px-1 py-12 sm:py-16 ${className}`}>{children}</section>
);
const H2 = ({ children }) => (
  <h2 className="text-xl sm:text-2xl font-bold text-[#102A43] font-['Outfit'] leading-snug mb-3 text-center">{children}</h2>
);
const BlueCta = ({ children, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="mx-auto flex items-center gap-2 px-7 h-12 bg-[#2563EB] text-white font-semibold rounded-full hover:bg-[#1D4ED8] active:scale-[0.98] transition-all">
    <Search className="w-4 h-4" />{children}
  </button>
);

export const HomeSections = ({ lang = 'fr', onSearchFocus }) => {
  const navigate = useNavigate();
  const t = T[lang] || T.fr;
  const rootRef = useRef(null);
  const seen = useRef(new Set());

  useEffect(() => {
    const map = { foryou_pro: 'professional_section_viewed', foryou_private: 'private_section_viewed', curiosity: 'curiosity_section_viewed', monitoring: 'monitoring_section_viewed', share: 'share_section_viewed' };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const id = e.target.getAttribute('data-section');
        if (e.isIntersecting && map[id] && !seen.current.has(id)) {
          seen.current.add(id);
          track(map[id]);
        }
      });
    }, { threshold: 0.3 });
    rootRef.current?.querySelectorAll('[data-section]').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const go = onSearchFocus;

  return (
    <div ref={rootRef} className="w-full" data-testid="home-sections">
      {/* 5. Pourquoi CheckCheck */}
      <Sec id="why">
        <H2>{t.whyTitle}</H2>
        <p className="text-sm text-slate-500 text-center leading-relaxed mb-7 max-w-lg mx-auto">{t.whyText}</p>
        <div className="grid grid-cols-2 gap-3 mb-7">
          {t.whyItems.map((it) => (
            <div key={it.t} className="p-4 rounded-2xl bg-white border border-slate-200">
              <it.icon className="w-5 h-5 text-[#2563EB] mb-2" />
              <p className="text-sm font-bold text-[#172033] mb-1">{it.t}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
        <BlueCta onClick={go} testid="why-cta">{t.whyCta}</BlueCta>
      </Sec>

      {/* 6. Exemple de rapport */}
      <Sec id="example" className="!py-8">
        <H2>{t.clearTitle}</H2>
        <div className="max-w-md mx-auto p-5 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="demo-report">
          <div className="flex items-center justify-between mb-1">
            <p className="font-bold text-[#102A43]">ENTREPRISE EXEMPLE</p>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">{t.fictional}</span>
          </div>
          <div className="flex items-end gap-2 mb-1">
            <span className="text-5xl font-bold text-[#16A34A] font-['Outfit']">84</span>
            <span className="text-slate-400 mb-1.5">/ 100</span>
          </div>
          <p className="flex items-center gap-1.5 text-sm text-[#16A34A] font-medium mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-[#16A34A]" />{t.solidLabel}
          </p>
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 mb-3">
            <p className="text-xs font-bold text-[#172033] mb-0.5">{t.inShort}</p>
            <p className="text-xs text-slate-500">{t.inShortText}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {t.indicators.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-slate-100">
                <span className="text-xs text-slate-500">{k}</span>
                <span className="text-xs font-semibold text-[#16A34A]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </Sec>

      {/* 8. Grille fonctionnalités */}
      <Sec id="features">
        <H2>{t.featTitle}</H2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {t.features.map(([Icon, k, d]) => (
            <div key={k} className="p-3.5 rounded-xl bg-white border border-slate-200">
              <Icon className="w-4 h-4 text-[#2563EB] mb-1.5" />
              <p className="text-xs font-bold text-[#172033] mb-0.5">{k}</p>
              <p className="text-[11px] text-slate-500 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Sec>

      {/* 9. Pour vous */}
      <Sec id="foryou_pro">
        <H2>{t.forYouTitle}</H2>
        <div className="space-y-3">
          {t.profiles.map(([Icon, title, text, type]) => (
            <button key={title} onClick={() => { track('profile_card_clicked', { type }); go(); }}
              className="w-full text-left p-4 rounded-2xl bg-white border border-slate-200 hover:border-[#2563EB]/40 transition-all flex gap-3"
              data-testid={`profile-card-${type}-${title.split(' ')[0].toLowerCase()}`}>
              <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4.5 h-4.5 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#172033] mb-0.5">{title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{text}</p>
              </div>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 text-center mt-4">{t.forYouNote}</p>
      </Sec>

      {/* 10. Curiosité */}
      <Sec id="curiosity" className="!py-10">
        <div className="p-6 rounded-2xl bg-[#102A43] text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white font-['Outfit'] leading-snug mb-2">{t.curioTitle}</h2>
          <p className="text-sm text-white/60 leading-relaxed mb-5 max-w-md mx-auto">{t.curioText}</p>
          <button onClick={go} data-testid="curiosity-cta"
            className="mx-auto flex items-center gap-2 px-6 h-11 bg-white text-[#102A43] font-semibold rounded-full hover:bg-slate-100 transition-all">
            <Search className="w-4 h-4" />{t.curioCta}
          </button>
        </div>
      </Sec>

      {/* 11. Surveillance */}
      <Sec id="monitoring">
        <H2>{t.watchTitle}</H2>
        <p className="text-sm text-slate-500 text-center leading-relaxed mb-5 max-w-md mx-auto">{t.watchText}</p>
        <div className="max-w-sm mx-auto space-y-2 mb-6">
          {[['Entreprise A', 87, 87, Minus, 'text-slate-400'], ['Entreprise B', 82, 76, TrendingDown, 'text-[#DC2626]'], ['Entreprise C', 71, 78, TrendingUp, 'text-[#16A34A]']].map(([n, a, b, Icon, c]) => (
            <div key={n} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white border border-slate-200">
              <span className="text-sm text-[#172033] font-medium">{n}</span>
              <span className={`flex items-center gap-1.5 text-sm font-semibold ${c}`}>{a} → {b} <Icon className="w-4 h-4" /></span>
            </div>
          ))}
        </div>
        <BlueCta onClick={go} testid="watch-cta"><Bell className="w-4 h-4" />{t.watchCta}</BlueCta>
      </Sec>

      {/* 12+13. Historique + Listes */}
      <Sec id="history" className="!py-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-5 rounded-2xl bg-white border border-slate-200">
            <History className="w-5 h-5 text-[#2563EB] mb-2" />
            <p className="text-sm font-bold text-[#172033] mb-1">{t.histTitle}</p>
            <p className="text-xs text-slate-500 leading-relaxed">{t.histText}</p>
          </div>
          <div className="p-5 rounded-2xl bg-white border border-slate-200">
            <FolderOpen className="w-5 h-5 text-[#2563EB] mb-2" />
            <p className="text-sm font-bold text-[#172033] mb-2">{t.listTitle}</p>
            <div className="flex flex-wrap gap-1.5">
              {t.lists.map((l) => <span key={l} className="text-[11px] px-2.5 py-1 rounded-full bg-[#2563EB]/10 text-[#1D4ED8] font-medium">{l}</span>)}
            </div>
          </div>
        </div>
      </Sec>

      {/* 14. Partage */}
      <Sec id="share">
        <H2>{t.shareTitle}</H2>
        <p className="text-sm text-slate-500 text-center leading-relaxed mb-5 max-w-md mx-auto">{t.shareText}</p>
        <div className="flex justify-center gap-3">
          {[[MessageCircle, 'WhatsApp'], [Mail, 'Email'], [Link2, 'Lien'], [Linkedin, 'LinkedIn']].map(([Icon, l]) => (
            <div key={l} className="flex flex-col items-center gap-1.5">
              <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#2563EB]" />
              </div>
              <span className="text-[10px] text-slate-500">{l}</span>
            </div>
          ))}
        </div>
      </Sec>

      {/* 15. Comment ça marche */}
      <Sec id="how">
        <H2>{t.howTitle}</H2>
        <div className="space-y-3 max-w-md mx-auto">
          {t.steps.map(([k, d], i) => (
            <div key={k} className="flex gap-3 p-4 rounded-2xl bg-white border border-slate-200">
              <span className="w-7 h-7 rounded-full bg-[#2563EB] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <div><p className="text-sm font-bold text-[#172033]">{k}</p><p className="text-xs text-slate-500 leading-relaxed">{d}</p></div>
            </div>
          ))}
        </div>
      </Sec>

      {/* 16. Gratuité */}
      <Sec id="free">
        <H2>{t.freeTitle}</H2>
        <p className="text-sm text-slate-500 text-center leading-relaxed mb-5 max-w-md mx-auto">{t.freeText}</p>
        <div className="grid grid-cols-2 gap-2 max-w-md mx-auto mb-6">
          {t.freeItems.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="w-4 h-4 text-[#16A34A] flex-shrink-0" />{f}
            </div>
          ))}
        </div>
        <BlueCta onClick={go} testid="free-cta">{t.freeCta}</BlueCta>
      </Sec>

      {/* 18. Confiance */}
      <Sec id="trust" className="!py-8">
        <h3 className="text-base font-bold text-[#102A43] text-center mb-2">{t.trustTitle}</h3>
        <p className="text-xs text-slate-400 text-center leading-relaxed max-w-md mx-auto">{t.trustText}</p>
      </Sec>

      {/* 19. Section finale */}
      <Sec id="final" className="!py-6">
        <div className="p-8 rounded-3xl bg-[#102A43] text-center" data-testid="final-cta-section">
          <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-1">{t.finalTitle}</h2>
          <p className="text-sm text-white/60 mb-5">{t.finalSub}</p>
          <button onClick={() => { track('bottom_search_started'); go(); }} data-testid="bottom-search-cta"
            className="mx-auto flex items-center gap-2 px-8 h-13 py-3.5 bg-[#2563EB] text-white font-bold rounded-full hover:bg-[#1D4ED8] transition-all">
            <Search className="w-5 h-5" />{t.finalCta}
          </button>
          <p className="text-xs text-white/40 mt-4">{t.finalTag}</p>
        </div>
      </Sec>
    </div>
  );
};
